import {useRef} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {DataTableColumn} from 'mantine-datatable';

import {useAccountsWithFilters} from '../hooks/useAccountsWithFilters';
import {useAccountsSelection} from '../hooks/useAccountsSelection';
import {useAccountsSearch} from '../hooks/useAccountsSearch';
import {useAccountsTableColumns} from '../hooks/useAccountsTableColumns';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {Account} from '../types';

export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions()

    const navigate = useNavigate();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useAccountsWithFilters();

    const {
        columns,
    } = useAccountsTableColumns();

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useAccounts owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useAccountsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useAccountsSelection();

    // Checkboxes appear only for an admin who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: Account[] = [];
    if (allowedActions.includes(rbacActions.DeleteAccount)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an admin who mistyped a
    // keyword that there are no accounts at all.
    const noRecordsText = keyword
        ? `No accounts match "${keyword}" in the records loaded so far.`
        : 'No accounts yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load accounts'>
                    The accounts API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) =>
                    navigate(`/accounts/${encodeURIComponent((record as Account).id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='account'
            />
        </Stack>
    );
};
